/**
 * Configuration globale axios pour le shop : CSRF via le header X-CSRF-TOKEN
 * lu dans <meta name="csrf-token">, plus jamais depuis document.cookie
 * (le cookie XSRF-TOKEN est HttpOnly côté serveur).
 *
 * Shannon 2026-04-22 ticket #127 finding P1 #8.
 *
 * Comme axios est un singleton ES-module, ces defaults sont visibles par
 * tous les `import axios from 'axios'` faits dans les composants/stores
 * tant que ce module est importé en premier (via custom.js).
 */
import axios from "axios";

const readCsrfToken = () => {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.getAttribute("content") : null;
};

const refreshCsrfToken = async () => {
    const { data } = await axios.get("/csrf-token", { withCredentials: true });

    if (data && data.token) {
        const meta = document.querySelector('meta[name="csrf-token"]');
        if (meta) {
            meta.setAttribute("content", data.token);
        }
        axios.defaults.headers.common["X-CSRF-TOKEN"] = data.token;
    }
};

axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";

// Force l'envoi des cookies même quand un proxy/CDN fait apparaître la requête
// comme cross-site (ex: ré-écriture d'origine via Cloudflare/nginx subdomain).
// Pour du pur same-origin c'est no-op, mais évite que XSRF-TOKEN/laravel_session
// ne soient muets quand la requête est qualifiée cross-origin par le browser.
axios.defaults.withCredentials = true;

const initialToken = readCsrfToken();
if (initialToken) {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = initialToken;
}

// Désactive la lecture auto du cookie XSRF-TOKEN (HttpOnly désormais).
axios.defaults.xsrfCookieName = null;
axios.defaults.xsrfHeaderName = null;

// Le HTML public peut être partagé : le widget privé hydrate le jeton après
// le démarrage du bundle. Relire la meta évite d'utiliser la valeur initiale.
axios.interceptors.request.use((config) => {
    const token = readCsrfToken();
    if (token) {
        config.headers["X-CSRF-TOKEN"] = token;
    }
    return config;
});

let refreshPromise = null;

axios.interceptors.response.use(
    (response) => response,
    async (error) => {
        const { config, response } = error || {};

        if (response && response.status === 419 && config && !config.__isRetry) {
            config.__isRetry = true;
            if (!refreshPromise) {
                refreshPromise = refreshCsrfToken().finally(() => {
                    refreshPromise = null;
                });
            }
            await refreshPromise;
            config.headers["X-CSRF-TOKEN"] = axios.defaults.headers.common["X-CSRF-TOKEN"];
            return axios.request(config);
        }

        return Promise.reject(error);
    }
);

export default axios;
